import { createTheme } from '@mui/material/styles';

// Define custom breakpoints that better match modern screen sizes
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// Define a modular scale for spacing
const spacing = 4; // Base spacing unit in pixels (reduced from 8)

// Define a modular scale for typography
const typographyScale = {
  h1: { fontSize: '2rem', lineHeight: 1.2, fontWeight: 600 },      // 32px
  h2: { fontSize: '1.5rem', lineHeight: 1.2, fontWeight: 600 },    // 24px
  h3: { fontSize: '1.25rem', lineHeight: 1.2, fontWeight: 600 },   // 20px
  h4: { fontSize: '1.125rem', lineHeight: 1.2, fontWeight: 600 },  // 18px
  h5: { fontSize: '1rem', lineHeight: 1.2, fontWeight: 600 },      // 16px
  h6: { fontSize: '0.875rem', lineHeight: 1.2, fontWeight: 600 },  // 14px
  h7: { fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 600 },  // 12px
  subtitle1: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 },     // 16px
  subtitle2: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 500 }, // 14px
  body1: { fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 400 },     // 14px
  body2: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 },      // 12px
  body3: { fontSize: '0.625rem', lineHeight: 1.5, fontWeight: 400 },      // 10px
  button: { fontSize: '0.875rem', lineHeight: 1.75, fontWeight: 500 },   // 14px
  caption: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 400 },    // 12px
  overline: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 500 },   // 12px
};

// Define semantic colors
const colors = {
  primary: {
    main: '#2196F3',      // Blue
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F50057',      // Pink
    light: '#FF4081',
    dark: '#C51162',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#FF3B30',      // Red
    light: '#FF6B6B',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FF9500',      // Orange
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0A84FF',      // Blue
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#34C759',      // Green
    light: '#4CAF50',
    dark: '#388E3C',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
    grey: '#F5F5F5',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Define consistent border radiuses
const shape = {
  borderRadius: 4,
  borderRadiusSmall: 4,
  borderRadiusLarge: 4,
};

// Define consistent shadows
const shadows = [
  'none',
  '0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.04),0px 1px 3px 0px rgba(0,0,0,0.02)',
  '0px 3px 1px -2px rgba(0,0,0,0.06),0px 2px 2px 0px rgba(0,0,0,0.04),0px 1px 5px 0px rgba(0,0,0,0.02)',
  '0px 3px 3px -2px rgba(0,0,0,0.06),0px 3px 4px 0px rgba(0,0,0,0.04),0px 1px 8px 0px rgba(0,0,0,0.02)',
  '0px 2px 4px -1px rgba(0,0,0,0.06),0px 4px 5px 0px rgba(0,0,0,0.04),0px 1px 10px 0px rgba(0,0,0,0.02)',
  // ... add more shadows as needed
];

// Define consistent transitions
const transitions = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

// Define consistent z-index values
const zIndex = {
  mobileStepper: 1000,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// Custom component overrides
const components = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '20px',
        padding: '8px 16px',
        fontSize: typographyScale.button.fontSize,
        fontWeight: typographyScale.button.fontWeight,
        lineHeight: typographyScale.button.lineHeight,
        minWidth: '75px',
        transition: 'all 0.2s ease',
        border: `1.5px solid ${colors.primary.main}`,
        color: colors.primary.main,
        backgroundColor: 'transparent',
        height: '30px',
        '&:hover': {
          backgroundColor: `${colors.primary.main}14`,
          borderColor: colors.primary.dark,
        },
      },
      contained: {
        border: `1.5px solid ${colors.primary.main}`,
        backgroundColor: 'transparent',
        color: colors.primary.main,
        height: '30px',
        '&:hover': {
          backgroundColor: `${colors.primary.main}14`,
          borderColor: colors.primary.dark,
          backgroundColor: 'transparent',
        },
      },
      outlined: {
        border: `1.5px solid ${colors.primary.main}`,
        color: colors.primary.main,
        backgroundColor: 'transparent',
        height: '30px',
        '&:hover': {
          backgroundColor: `${colors.primary.main}14`,
          borderColor: colors.primary.dark,
          border: `1.5px solid ${colors.primary.dark}`,
        },
      },
      text: {
        border: `1.5px solid ${colors.primary.main}`,
        color: colors.primary.main,
        backgroundColor: 'transparent',
        height: '30px',
        '&:hover': {
          backgroundColor: `${colors.primary.main}14`,
          borderColor: colors.primary.dark,
        },
      },
    },
    variants: [
      {
        props: { variant: 'message' },
        style: {
          border: `1.5px solid ${colors.primary.main}`,
          color: colors.primary.main,
          backgroundColor: 'transparent',
          height: '30px',
          minWidth: '75px',
          '&:hover': {
            backgroundColor: `${colors.primary.main}14`,
            borderColor: colors.primary.dark,
          },
        },
      },
      {
        props: { variant: 'manage' },
        style: {
          border: `1.5px solid ${colors.primary.main}`,
          color: colors.primary.main,
          backgroundColor: 'transparent',
          height: '30px',
          minWidth: '75px',
          '&:hover': {
            backgroundColor: `${colors.primary.main}14`,
            borderColor: colors.primary.dark,
          },
        },
      },
      {
        props: { variant: 'danger' },
        style: {
          border: `1.5px solid ${colors.error.main}`,
          color: colors.error.main,
          backgroundColor: 'transparent',
          height: '30px',
          minWidth: '75px',
          '&:hover': {
            backgroundColor: `${colors.error.main}14`,
            borderColor: colors.error.dark,
          },
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadius,
        boxShadow: shadows[1],
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '&.MuiInputBase-multiline': {
            height: 'auto',
          },
          '&:not(.MuiInputBase-multiline)': {
            height: '36px',
          },
        },
      },
    },
  },
};

// Add custom dashboard panel styles
const custom = {
  dashboardPanel: {
    background: colors.background.paper,
    borderRadius: shape.borderRadiusLarge,
    boxShadow: shadows[1],
    border: `1px solid ${colors.grey[300]}`,
    transition: 'box-shadow 0.2s, border-color 0.2s',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      boxShadow: shadows[3],
      borderColor: colors.primary.light,
    },
  },
  dashboardPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    background: colors.primary.white,
    borderBottom: `1px solid ${colors.grey[300]}`,
    borderTopLeftRadius: shape.borderRadiusLarge,
    borderTopRightRadius: shape.borderRadiusLarge,
    px: 2,
    py: 1,
    fontWeight: 600,
    fontSize: typographyScale.h5.fontSize,
    cursor: 'grab',
    userSelect: 'none',
  },
};

// Create and export the theme
const theme = createTheme({
  breakpoints,
  spacing,
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    ...typographyScale,
  },
  palette: colors,
  shape,
  shadows,
  transitions,
  zIndex,
  components,
  custom,
});

export default theme; 